import Keycloak, { type KeycloakTokenParsed } from "keycloak-js";
import { z } from "zod";
import { clearAuthState, setAuthState } from "@/lib/auth-store";

type TokenClaims = KeycloakTokenParsed & {
  email?: string;
  name?: string;
  preferred_username?: string;
};
const tokenClaimsSchema = z.looseObject({
  exp: z.number().optional(),
  email: z.string().optional(),
  name: z.string().optional(),
  preferred_username: z.string().optional(),
});

const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL?.trim()
  || (import.meta.env.DEV ? "http://localhost:8180" : "https://bpm.grupmontaner.com/auth");
const keycloakRealm = import.meta.env.VITE_KEYCLOAK_REALM?.trim() || "camunda";
const keycloakClientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID?.trim() || "camunda-frontend";

export const keycloak = new Keycloak({
  url: keycloakUrl,
  realm: keycloakRealm,
  clientId: keycloakClientId,
});

let initialization: Promise<boolean> | null = null;

export const initializeKeycloak = (): Promise<boolean> => {
  initialization ??= keycloak.init({
    onLoad: "check-sso",
    flow: "standard",
    pkceMethod: "S256",
    checkLoginIframe: false,
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  });
  return initialization;
};

export const readKeycloakClaims = (): TokenClaims => {
  const claims = tokenClaimsSchema.safeParse(keycloak.tokenParsed ?? {});
  return claims.success ? claims.data : {};
};

export const syncKeycloakAuthStore = (): boolean => {
  if (!keycloak.authenticated || !keycloak.token) {
    clearAuthState();
    return false;
  }

  const claims = readKeycloakClaims();
  setAuthState({
    accessToken: keycloak.token,
    idToken: keycloak.idToken ?? null,
    preferredUsername: claims.preferred_username?.trim() || null,
    expiresAt: claims.exp ?? null,
  }, { sessionConfirmed: true });
  return true;
};

export const refreshKeycloakSession = async (minimumValiditySeconds = 60): Promise<boolean> => {
  if (!keycloak.authenticated) {
    clearAuthState();
    return false;
  }

  try {
    await keycloak.updateToken(minimumValiditySeconds);
    return syncKeycloakAuthStore();
  } catch {
    keycloak.clearToken();
    clearAuthState();
    return false;
  }
};
